import axios from "axios";
import { useContext, useEffect, useId, useState } from "react";
import { motion } from "framer-motion";
import ReactGA from "react-ga4";

import { Search2Icon } from "@chakra-ui/icons";
import { Box, Input, InputGroup, InputLeftElement, Text, useColorMode } from "@chakra-ui/react";

import { CoffeeShopContext } from "../../context/CoffeeShopContext";

const MAPBOX_TYPES = "neighborhood,district,locality,place,address";

// Derive a search radius from the Mapbox bbox of a geocoded feature.
// Larger areas (cities) → larger radius; small neighborhoods → tighter radius.
const bboxToRadius = (bbox) => {
  if (!bbox) return null;
  const [minLng, minLat, maxLng, maxLat] = bbox;
  const centerLat = (minLat + maxLat) / 2;
  const latDist = (maxLat - minLat) * 111000;
  const lngDist = (maxLng - minLng) * 111000 * Math.cos((centerLat * Math.PI) / 180);
  const diagonal = Math.sqrt(latDist ** 2 + lngDist ** 2);
  return Math.min(Math.max(Math.round(diagonal / 2), 500), 15000);
};

const getDisplayLabel = (feature) => {
  const { text, context = [] } = feature;
  const city = context.find((c) => c.id.startsWith("locality") || c.id.startsWith("place"));
  const region = context.find((c) => c.id.startsWith("region"));
  const country = context.find((c) => c.id.startsWith("country"));

  const isUS =
    country?.short_code?.toLowerCase() === "us" ||
    region?.short_code?.toUpperCase().startsWith("US-");

  // US: "Brooklyn, NY" — state abbrev is familiar. Elsewhere region codes like
  // ES-B (Barcelona province) are opaque; prefer country name → "Barcelona, Spain".
  if (isUS) {
    const state = region?.short_code?.replace(/^US-/i, "") || region?.text;
    const secondary = city?.text && city.text !== text ? city.text : null;
    return [text, secondary, state].filter(Boolean).join(", ");
  }

  const secondary = city?.text && city.text !== text ? city.text : null;
  return [text, secondary, country?.text].filter(Boolean).join(", ");
};

const SearchBar = ({ onPlaceSelected, solid = false, autoFocus = false }) => {
  const { colorMode } = useColorMode();
  const { updateUserLocation, userLocation } = useContext(CoffeeShopContext);
  const [inputValue, setInputValue] = useState(userLocation?.label || "");
  const [query, setQuery] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [active, setActive] = useState(-1);
  const [status, setStatus] = useState("");
  const listId = useId();
  const isDark = colorMode === "dark";
  const accent = isDark ? "#c8a97e" : "#6a4020";
  const dropdownBg = isDark ? "#241910" : "#fdfaf5";
  // solid = nested in the homepage search well, which owns the surface and the hover treatment.
  const hoverShadow = isDark
    ? "0 0 0 1.5px rgba(200,169,126,0.22), 0 4px 18px rgba(200,169,126,0.09)"
    : "0 0 0 1.5px rgba(139,62,18,0.14), 0 4px 18px rgba(139,62,18,0.08)";
  const underline = isDark
    ? "linear-gradient(90deg, rgba(200,169,126,0) 0%, rgba(200,169,126,0.5) 50%, rgba(200,169,126,0) 100%)"
    : "linear-gradient(90deg, rgba(139,62,18,0) 0%, rgba(139,62,18,0.55) 50%, rgba(139,62,18,0) 100%)";

  useEffect(() => { setInputValue(userLocation?.label || ""); }, [userLocation]);
  useEffect(() => {
    if (query === null || query.trim().length < 2) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setStatus("Searching places…");
      try {
        const response = await axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`, {
          params: { access_token: process.env.REACT_APP_MAPBOX_TOKEN, proximity: "ip", limit: 5, types: MAPBOX_TYPES, language: "en" },
        });
        if (cancelled) return;
        const features = response.data.features || [];
        setSuggestions(features);
        setStatus(features.length ? "" : "No places found. Try another neighborhood or city.");
      } catch {
        if (!cancelled) setStatus("Place search is unavailable. Check your connection and try again.");
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);

  const dismiss = () => { setQuery(null); setSuggestions([]); setActive(-1); setStatus(""); };
  const select = (feature) => {
    const label = getDisplayLabel(feature);
    dismiss();
    setInputValue(label);
    const [longitude, latitude] = feature.center;
    updateUserLocation({ latitude, longitude, label, radius: bboxToRadius(feature.bbox) });
    ReactGA.event({ category: "Search", action: "Location Selected", label });
    onPlaceSelected?.(feature.center);
  };
  const onKeyDown = (event) => {
    if (event.key === "Escape") { dismiss(); return; }
    if (!suggestions.length) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => event.key === "ArrowDown" ? (index + 1) % suggestions.length : (index <= 0 ? suggestions.length - 1 : index - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      select(suggestions[active < 0 ? 0 : active]);
    }
  };
  return <Box width="100%" maxW={solid ? "none" : "480px"} position="relative">
    <Box
      position="relative"
      borderRadius="20px"
      transition="box-shadow 0.2s ease"
      _hover={solid ? undefined : { boxShadow: hoverShadow }}
      sx={solid ? undefined : { "&:hover .search-underline": { opacity: 1, transform: "scaleX(1)" } }}
    >
      <InputGroup size={{ base: "md", md: "lg" }}>
        <InputLeftElement pointerEvents="none"><Search2Icon opacity={0.5} boxSize={{ base: "14px", md: "16px" }} /></InputLeftElement>
        <Input role="combobox" aria-label="Neighborhood or city" aria-autocomplete="list" aria-expanded={suggestions.length > 0} aria-controls={suggestions.length ? listId : undefined} aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined} autoFocus={autoFocus} autoComplete="off" value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); setQuery(e.target.value); setSuggestions([]); setActive(-1); setStatus(""); }}
          onBlur={dismiss} onKeyDown={onKeyDown} placeholder="Neighborhood or city"
          fontSize="16px" borderRadius="20px" textAlign="left"
          bg={solid ? "transparent" : (isDark ? "transparent" : "white")}
          border={solid ? "none" : undefined}
          borderColor={solid ? "transparent" : (isDark ? "whiteAlpha.200" : "transparent")}
          boxShadow={solid ? "none" : undefined}
          _placeholder={{ opacity: 0.5, color: "inherit" }}
          _hover={solid ? { borderColor: "transparent", boxShadow: "none" } : undefined}
          focusBorderColor={solid ? "transparent" : (isDark ? "coffee.100" : "transparent")}
          _focusVisible={{ outline: `2px solid ${accent}`, outlineOffset: "3px" }} />
      </InputGroup>
      {!solid && <Box className="search-underline" position="absolute" bottom="1px" left="12px" right="12px" h="1.5px" borderRadius="1px" pointerEvents="none" opacity={0} transform="scaleX(0)" transformOrigin="left center" background={underline} transition="opacity 0.25s ease, transform 0.3s ease" />}
    </Box>
    {suggestions.length > 0 && <Box as="ul" id={listId} role="listbox" aria-label="Places" position="absolute" w="100%" zIndex={1500} mt={2} p={2} bg={dropdownBg} border={isDark ? "1px solid" : "none"} borderColor="whiteAlpha.100" borderRadius="16px" boxShadow="0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06)" listStyleType="none" textAlign="left">
      {suggestions.map((feature, index) => {
        const isNeighborhood = feature.place_type?.[0] === "neighborhood" || feature.place_type?.[0] === "district";
        return <Box as="li" id={`${listId}-${index}`} role="option" aria-selected={active === index} key={feature.id} position="relative" overflow="hidden" borderRadius="10px" minH="44px" cursor="pointer" onMouseEnter={() => setActive(index)} onMouseDown={(e) => e.preventDefault()} onClick={() => select(feature)}>
          <motion.div animate={{ opacity: active === index ? 1 : 0 }} transition={{ duration: 0.15 }} style={{ position: "absolute", inset: 0, borderRadius: "10px", background: isDark ? "rgba(200,169,126,0.09)" : "rgba(139,62,18,0.07)", pointerEvents: "none" }} />
          <motion.div animate={{ scaleY: active === index ? 1 : 0, opacity: active === index ? 1 : 0 }} transition={{ duration: 0.18, ease: "easeOut" }} style={{ position: "absolute", left: 0, top: "12%", bottom: "12%", width: "2.5px", borderRadius: "2px", transformOrigin: "center", background: isDark ? "rgba(200,169,126,0.75)" : "rgba(139,62,18,0.55)", boxShadow: isDark ? "0 0 8px rgba(200,169,126,0.5)" : "0 0 8px rgba(139,62,18,0.35)", pointerEvents: "none" }} />
          <Text px={3} py={3} fontSize="sm" fontWeight={isNeighborhood ? 500 : 400} position="relative">{getDisplayLabel(feature)}</Text>
        </Box>;
      })}
    </Box>}
    <Box role="status" aria-live="polite">{status && <Text position="absolute" top="100%" mt={2} zIndex={1500} bg={dropdownBg} border={isDark ? "1px solid" : "none"} borderColor="whiteAlpha.100" borderRadius="16px" boxShadow="0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06)" px={4} py={3} w="100%" fontSize="sm" textAlign="left">{status}</Text>}</Box>
  </Box>;
};
export default SearchBar;
